// ** React Imports
import { useState, useEffect } from "react";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { toast } from "helpers/toastWrapper";

// ** Store & Actions
import { useSelector } from "react-redux";

// ** Custom Components
import Block from "components/layout/Block";
import TextField from "components/layout/TextField";
import Checkbox from "components/layout/Checkbox";
import { ButtonPrimary } from "components/layout/Button/ButtonPrimary";
import Fieldset from "components/layout/Fieldset";
import Tooltip from "components/layout/Tooltip";

// ** Utils & Helpers
import api from "helpers/ApiClient";

const StyledInputWrapper = styled.div`
	position: relative;
	width: 100%;
	height: 2.5rem;
	border: 1px solid;
	border-color: var(--dz-sys-input-border-color);
	border-radius: var(--dz-sys-input-border-radius);

	&:hover,
	&:focus-within,
	&:focus-visible {
		border-color: var(--dz-sys-input-border-color-hover);
		box-shadow: var(--dz-sys-input-shadow);
	}

	${TextField} {
		padding-right: 183px;
		border: 0;
		height: 33px;
		box-shadow: none;
		background-color: transparent;
	}
`;

const StyledButtonsWrapper = styled.div`
	position: absolute;
	top: 0px;
	right: -1px;
	display: flex;
	align-items: center;
	height: 33px;
	background-color: var(--dz-sys-input-background-color);

	label {
		height: 33px;
	}
`;

const CreateAlbum = ({ albumInfo = {}, getAlbums }) => {
	const [value, setValue] = useState("");
	const [isPrivate, setPrivate] = useState(false);
	const { t } = useTranslation(["userpanel", "common", "suspensions"]);

	const suspension = useSelector((state) => state.globalUser?.suspension);
	const isBan = suspension?.duration > 0;
	const tooltipInfo = isBan ? t("suspensions:bannedAccount") : "";

	const handleChange = (e) => setValue(e.target.value);

	useEffect(() => {
		if (albumInfo.name) {
			setValue(albumInfo.name);
			setPrivate(albumInfo.private);
		}
		// eslint-disable-next-line
	}, [albumInfo.name]);

	return (
		<Block mb={4}>
			<Fieldset
				legend={t("userpanel:media.album.albumName")}
				legendBold={false}
			>
				<StyledInputWrapper>
					<Tooltip
						wrapperStyle={{ width: "100%" }}
						text={tooltipInfo}
					>
						<TextField
							value={value}
							onChange={handleChange}
							disabled={isBan}
						/>
					</Tooltip>
					<StyledButtonsWrapper>
						<Checkbox
							labelText={t("userpanel:videos.private")}
							checked={isPrivate}
							onChange={(e) => setPrivate(e.target.checked)}
							customPaddingAllDevices="0.75rem 1rem"
							disabled={isBan}
							correctPosition
						/>
						<Tooltip
							text={tooltipInfo}
							wrapperStyle={{
								position: "relative",
								left: 0,
								right: 0,
								top: 0,
								bottom: 0,
							}}
						>
							<ButtonPrimary
								label={
									albumInfo.name
										? t("common:save")
										: t("userpanel:albumchanged")
								}
								onClick={() => {
									if (albumInfo.name) {
										api.put("/album", {
											id: albumInfo.id,
											name: value,
											private: isPrivate,
										})
											.then(() => {
												getAlbums();
												toast.success(
													t("userpanel:album.changed")
												);
											})
											.catch((error) => {
												if (
													error?.response?.data?.error
												) {
													toast.error(
														`${error.response.data.error}`
													);
												}
											});
									} else {
										api.post("/album", {
											name: value,
											private: isPrivate,
										})
											.then(() => {
												setValue("");
												setPrivate(false);
												getAlbums();
											})
											.catch((error) => {
												if (
													error?.response?.data?.error
												) {
													toast.error(
														`${error.response.data.error}`
													);
												}
											});
									}
								}}
								disabled={isBan}
								noBoxShadow
							/>
						</Tooltip>
					</StyledButtonsWrapper>
				</StyledInputWrapper>
			</Fieldset>
			<Block flex justifyEnd>
				<ButtonPrimary
					label={`${t("userpanel:photos.delete")}  ${t(
						"userpanel:photos.album"
					).toLowerCase()}`}
					onClick={() => {
						api.delete(`/album`, {
							params: {
								id: albumInfo.id,
							},
						})
							.then(() => {
								getAlbums({ withRedirect: true });
							})
							.catch((error) => {
								if (error?.response?.data?.error) {
									toast.error(`${error.response.data.error}`);
								}
							});
					}}
					disabled={isBan}
				/>
			</Block>
		</Block>
	);
};

export default CreateAlbum;
